import { useMemo } from "react";
import axios, { AxiosRequestConfig } from "axios"
import { Coach } from "../models/Coach";
import { getStorageItem } from "../utils/localStorage";
import { EndpointWithPagination } from "../typings/Pagination";
import { User } from "../models/User";
import { Exercise } from "../models/Exercise";
import { GroupExercise } from "../models/GroupExercise";
import { Languages } from "../typings/Languages";
import { PopulatedWorkout, Workout } from "../models/Workout";
import { GroupGym } from "../models/GroupGym";
import { UserGym } from "../models/UserGym";
import { BlockProgram } from "../models/Block";
import { BlockType } from "../enums/BlockType";
import { IProgram } from "../models/Program";
import { IAxiosResponses } from "../typings/AxiosRespnses";
import { ISpace } from "../models/Space";
import { ITeam } from "../models/Team";
import { EntitiesResponse } from "../pages/SelectEntityPage/SelectEntityPage";
import { IUser2 } from "../models/user copy";
import { IEndpointsResponse } from "../typings/endpoints";
import { IEvent } from "../models/event";
import { IDetailsTeamResponse, ITeamPulse } from "../models/endpoints/Team";
import { ICmjEventResult, IEventResult, IRpeEventResult } from "../models/eventResult";
import { ITestDefinition } from "../models/testDefinition";
import { ITestResult } from "../models/testResult";
import { ITeamTestConfig } from "../models/teamTestConfig";
import { IRTPProtocol, IRTPPhaseStatus } from "../models/rtpProtocol";
import { ITestingAnalytics } from "../models/testingAnalytics";
import {
  IProgrammingExercise,
  IProgrammingGroupExercise,
  IBlock,
  IWorkout,
  IWorkoutAssignment,
  IWorkoutLog,
  IAthleteFeedback,
  ISessionAnalytics,
  IAthleteAnalytics,
  ITeamAnalytics,
  IWeightHistory,
  IProgrammingPaginated,
} from "../models/programming";
import { IBodyMapTeamAnalytics } from "../models/bodyMapAlert";
import { fieldProgrammingEndpoints } from "../endpoints/fieldProgramming";
import { buildTacticalSchemasEndpoints } from "../endpoints/tacticalSchemas";

export interface INotificationItem {
  type: "event-to-compile" | "frc-to-compile" | "hrv-to-compile" | "hooper-to-compile";
  priority: number;
  data?: any; // IEvent
}
export type INotificationsResponse = INotificationItem[];

// export const baseUrl = "http://localhost:8000/api"
export const baseUrl = process.env.REACT_APP_BASE_URL;
// const baseUrl = "https://5d7b-188-217-166-80.ngrok-free.app"
const basicAuth = btoa(`${"com.development.fitness.dev"}:${"fitnessdevelopment"}`);

export type UserCoachBody = Omit<
  User,
  | "_id"
  | "bodyComposition"
  | "role"
  | "resetPasswordToken"
  | "createdAt"
  | "status"
  | "coach"
  | "updatedAt"
>;
export type ExerciseCoachBody = Omit<
  Exercise<string, string>,
  | "idCoach"
  | "common"
  | "_id"
> & {common?: boolean};
export type GroupGymBody = Omit<
  GroupGym,
  | "idGym"
> & {common?: boolean};

type QueryParams = Record<any, any>


export const useEndpoints = () => useMemo(() => ({
// Generico: usato per chiamate GET verso path non ancora coperti da un metodo dedicato
get: <T = any>(url: string, config?: AxiosRequestConfig) => axios.get<T>(`${baseUrl}${url}`, config),
checkConfirmToken: (id: string, confirmedToken: string) => axios.post<{code: number}>(`${baseUrl}/director/check-confirm-token`, {confirmedToken, id}),
confirmDirector: (body: Record<"password" | "confirmedToken", string>) => axios.post<{code: number}>(`${baseUrl}/director/confirm-register`, body),
loginDirctor: (body: {email: string, password: string}) => axios.post(`${baseUrl}/director/login`, body, {headers: {Authorization: `Basic ${basicAuth}`}}),
getDirectorTrainers: (params: any) => axios.get(`${baseUrl}/director/athlete-trainers`, {params}),
postTeam: (body: any) => axios.post<ITeam>(`${baseUrl}/teams`, body),
getTeam: (id: string) => axios.get<ITeam>(`${baseUrl}/teams/${id}`),
getSubjectReadiness: (params?: object) => axios.get<(IUser2 & {hooperQuestionnaire?: any})[]>(`${baseUrl}/subject-readiness`, {params}),
getSubjectReadinessTeam: (id: string, params?: object) => axios.get<(IUser2 & {hooperQuestionnaire?: any})[]>(`${baseUrl}/teams/${id}/subjective-readiness`, { params }),
getReadiness: (athleteId: string, params?: object) => axios.get(`${baseUrl}/readiness/${athleteId}`, { params }),
getReadinessHistory: (athleteId: string, params?: object) => axios.get(`${baseUrl}/readiness/${athleteId}/history`, { params }),
getTeamReadiness: (teamId: string, params?: object) => axios.get(`${baseUrl}/readiness/team/${teamId}`, { params }),

// Body Map
postBodyMap: (body: any) =>
  axios.post(`${baseUrl}/body-map`, body),
getBodyMapToday: () =>
  axios.get(`${baseUrl}/body-map/today`),
getBodyMapTeam: (teamId: string, params?: object) =>
  axios.get(`${baseUrl}/body-map/team/${teamId}`, { params }),
getBodyMapTeamDaily: (teamId: string, params?: object) =>
  axios.get(`${baseUrl}/body-map/team/${teamId}/daily`, { params }),
getBodyMapAthlete: (athleteId: string, params?: object) =>
  axios.get(`${baseUrl}/body-map/${athleteId}`, { params }),

// Alerts
getAlerts: (params?: object) =>
  axios.get(`${baseUrl}/alerts`, { params }),
getAlertsUnreadCount: () =>
  axios.get<{ count: number }>(`${baseUrl}/alerts/unread-count`),
patchAlertRead: (id: string) =>
  axios.patch(`${baseUrl}/alerts/${id}/read`),
patchAlertsReadAll: () =>
  axios.patch(`${baseUrl}/alerts/read-all`),

// Body Map Alerts (Fastidi fisici)
getBodyMapAlerts: (params?: object) =>
  axios.get(`${baseUrl}/body-map/alerts`, { params }),
getBodyMapAlertsUnreadCount: (params?: object) =>
  axios.get<{ count: number }>(`${baseUrl}/body-map/alerts/unread-count`, { params }),
postBodyMapAlertAcknowledge: (id: string) =>
  axios.post(`${baseUrl}/body-map/alerts/${id}/acknowledge`),
getBodyMapWeeklySummary: (athleteId: string) =>
  axios.get<{ zoneCount: number; persistentCount: number; domsCappedCount: number }>(`${baseUrl}/body-map/athlete/${athleteId}/weekly-summary`),
getBodyMapTeamAnalytics: (teamId: string, params?: { from?: string; to?: string }) =>
  axios.get<IBodyMapTeamAnalytics>(`${baseUrl}/body-map/team/${teamId}/analytics`, { params }),

/**
 * Drill-down heatmap: atleti del team con segnalazioni su una zona specifica
 * negli ultimi N giorni.
 */
getBodyMapTeamZone: (teamId: string, zone: string, params?: { period?: string }) =>
  axios.get(`${baseUrl}/body-map/team/${teamId}/zone/${zone}`, { params }),

// CV HRV
getCvHrv: (athleteId: string, params?: object) =>
  axios.get(`${baseUrl}/readiness/${athleteId}/cv-hrv`, { params }),

// Compliance
getTeamCompliance: (teamId: string, params?: object) =>
  axios.get(`${baseUrl}/readiness/team/${teamId}/compliance`, { params }),

// Effort — tab Sforzo (carico interno sRPE)
// spec: docs/superpowers/specs/2026-07-26-tab-sforzo-design.md
getTeamEffort: (teamId: string, params?: { week?: string }) =>
  axios.get(`${baseUrl}/effort/team/${teamId}`, { params }),
// Recovery settimanale team — vista Recovery dentro il tab Sforzo (incrocio recovery↔carico)
getTeamRecoveryWeek: (teamId: string, params?: { week?: string }) =>
  axios.get(`${baseUrl}/readiness/team/${teamId}/week`, { params }),
// endpoint atleta: leopart-update/endpoints/effort-athlete.ts (unica definizione)

postSpace: (body: any) => axios.post(`${baseUrl}/spaces`, body),

refreshUserToken: (id: string) => axios.post(`${baseUrl}/users/refresh-token/${id}`),


deleteTeam: (id: string) => axios.delete(`${baseUrl}/teams/${id}`),
postAthlete: (body: any) => axios.post(`${baseUrl}/athletes`, body),
patchTeam: (id: string,body: Partial<ITeam<string, string>>) => axios.patch(`${baseUrl}/teams/${id}`, body),
patchSpace: (id: string,body: Partial<any>) => axios.patch(`${baseUrl}/spaces/${id}`, body),
patchImageTeam: (id: string, body: any) => axios.post(`${baseUrl}/teams/image/${id}`, body),
patchImageUser: (email: string, body: any) => axios.post(`${baseUrl}/image-users/${email}`, body),
postUser: (body: any) => axios.post(`${baseUrl}/users`, body,{headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
patchUser: (id: string, body: any) => axios.patch(`${baseUrl}/users/${id}`, body,{headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
verifyToken: (body: { token: string }) => axios.post<IEndpointsResponse["verifyToken"]>(`${baseUrl}/users/verify-token`, body),
confirmToken: (body: any) => axios.post(`${baseUrl}/users/confirm-token`, body),
deleteUser: (id: string) => axios.delete(`${baseUrl}/users/${id}`),
resetDirectorPassword: (id: string) => axios.post(`${baseUrl}/directors/reset-password/${id}`),
forgotPassword: (email: string) => axios.post(`${(baseUrl ?? '').replace(/\/api$/, '')}/api/auth/forgot-password`, { email }),
patchDirector: (id: string, body: any) => axios.patch(`${baseUrl}/directors/${id}`, body),
getKeycloakUser: (id: string) => axios.get<any>(`${baseUrl}/users/keycloak/${id}`),
patchKeycloakUser: (body: any) => axios.patch(`${baseUrl}/users/keycloak`, body),

postEvent: (body: any) => axios.post(`${baseUrl}/events`, body),
patchEvent: (id: string, body: any) => axios.patch(`${baseUrl}/events/${id}`, body),
deleteEvent: (id: string, scope?: string) => axios.delete(`${baseUrl}/events/${id}`, { params: scope ? { scope } : {}, headers: { Authorization: `Basic ${basicAuth}` } }),
deleteEventOccurrence: (masterId: string, originalStart: string, scope: string) => axios.delete(`${baseUrl}/events/${masterId}/occurrences`, { params: { originalStart, scope }, headers: { Authorization: `Basic ${basicAuth}` } }),
patchEventOccurrence: (masterId: string, originalStart: string, scope: string, body: any) => axios.patch(`${baseUrl}/events/${masterId}/occurrences`, body, { params: { originalStart, scope }, headers: { Authorization: `Basic ${basicAuth}` } }),
getEvents: (params?: AxiosRequestConfig<any>["params"]) => axios.get<EndpointWithPagination<IEvent>>(`${baseUrl}/events`, {params}),
getEvent: (id: string, params?: AxiosRequestConfig<any>["params"]) => axios.get<IEvent>(`${baseUrl}/events/${id}`, {params}),

getNotifications: () => axios.get<INotificationsResponse>(`${baseUrl}/notifications`),
registerNotificationToken: (token: string, os: "ios" | "android") => axios.post(`${baseUrl}/notifications/register-token`, { token, os }),
// Sollecito globale (#17): push agli atleti con attività in sospeso (Hooper +
// eventi non compilati). teamId assente = tutti i team del coach/director.
remindGlobal: (teamId?: string) => axios.post<{ sent: number; failed: number; total: number }>(`${baseUrl}/notifications/remind-global`, { teamId }),


getEventResults: (params?: AxiosRequestConfig<any>["params"]) => axios.get<EndpointWithPagination<IEventResult>>(`${baseUrl}/eventresults`, {params}),


checkConfirmAthleteToken: (id: string, confirmedToken: string) => axios.post<{code: number}>(`${baseUrl}/athletes/check-confirm-token`, {confirmedToken, id}),
confirmAthlete: (body: Record<"password" | "confirmedToken", string>) => axios.post<{code: number}>(`${baseUrl}/athletes/confirm-register`, body),
deleteAthlete: (id: string) => axios.delete(`${baseUrl}/athletes/${id}`),

getEntities: (email: string) => axios.get<EntitiesResponse>(`${baseUrl}/directors/entities?email=${email}`),

getTeams: (params: any) => axios.get<EndpointWithPagination<any>>(`${baseUrl}/teams/`, {params}),

getSpaces: (params: any) => axios.get<EndpointWithPagination<ISpace>>(`${baseUrl}/spaces/`, {params}),
getSpace: (id: string, params?: any) => axios.get<ISpace>(`${baseUrl}/spaces/${id}`, {params}),
getAthletes: (params: any) => axios.get(`${baseUrl}/athletes/`, {params}),
getUsers: (params: any) => axios.get<EndpointWithPagination<IUser2>>(`${baseUrl}/users`, {params}),



deleteSpace: (id: string) => axios.delete(`${baseUrl}/spaces/${id}`),


postEventResult:  ( data?: IRpeEventResult | IRpeEventResult | ICmjEventResult ) => axios.post(`${baseUrl}/eventresults`, data),
postEventResultBulk: (data: any[]) => axios.post<Array<{ index: number; success: boolean; data?: any; error?: string }>>(`${baseUrl}/eventresults/bulk`, data),
patchEventResult: (id: string, body: any) => axios.patch(`${baseUrl}/eventresults/${id}`, body),



// ------------------- //

loginCoach: (body: {email: string, password: string}) => axios.post(`${baseUrl}/director/login`, body, {headers: {Authorization: `Basic ${basicAuth}`}}),

registerCoach: (coach: Pick<Coach,"firstName" | "lastName" | "password" | "email" | "phone">) => axios.post(`${baseUrl}/gyms`, coach, {headers: {Authorization: `Basic ${basicAuth}`}}),
getUsersCoach: (params?: unknown) => axios.get<EndpointWithPagination<UserGym>>(`${baseUrl}/gyms/users`, { params, headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
getGroups: (params?: unknown) => axios.get<EndpointWithPagination<GroupGym>>(`${baseUrl}/groups-gym`, { params, headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
getUser: (id: string) => axios.get<User>(`${baseUrl}/users/${id}`, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
getUserGym: (id: string) => axios.get<UserGym>(`${baseUrl}/gyms/users/${id}`, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
getExistUser: (email: string) => axios.post<{exist: boolean, id?: string}>(`${baseUrl}/gyms/exist-user-gym`, {email},  {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
getWorkouts: (params?: Record<'limit' | 'skip', number>, queryParams: QueryParams = {}) => axios.get<EndpointWithPagination<PopulatedWorkout>>(`${baseUrl}/workouts`, { params: {...params, ...queryParams},headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
getExercisesCoach: (params?: Record<'limit' | 'skip', number> & {language?: Languages, common?: boolean, search?: string}) => axios.get<EndpointWithPagination<Exercise>>(`${baseUrl}/gyms/exercises`, {params, headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
getExercises: (params?: Record<'limit' | 'skip', number> & {language?: Languages, search?: string}) => axios.get<EndpointWithPagination<Exercise<Coach>>>(`${baseUrl}/exercises`,  {params, headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
patchImageExercise: (formData: FormData, id: string) => axios.post(`${baseUrl}/gyms/exercises-image/${id}`, formData, { headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
patchPreviewExercise: (formData: FormData, id: string) => axios.post(`${baseUrl}/gyms/exercises-preview/${id}`, formData, { headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
patchVideoExercise: (formData: FormData, id: string) => axios.post(`${baseUrl}/gyms/exercises-video/${id}`, formData, { headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
getGroupsExercises: (params?: Record<'limit' | 'skip', number>  & {language?: Languages, search?: string}, queryParams: QueryParams = {}) => axios.get<EndpointWithPagination<Exercise<GroupExercise>>>(`${baseUrl}/groups-exercise`,  {params: {...params, ...queryParams}, headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
postUserCoach: (id: string, body: UserCoachBody | {idUser: string}) => axios.post(`${baseUrl}/gyms/${id}/users`, body,{headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
patchUserCoach: (id: string, idUser: string, body: UserCoachBody) => axios.patch(`${baseUrl}/gyms/${id}/users/${idUser}`, body,{headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
patchCoach: (id: string, body: UserCoachBody) => axios.patch(`${baseUrl}/gyms/${id}`, body,{headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
postExerciseCoach: (body: ExerciseCoachBody) => axios.post<Record<'id' | 'message', string>>(`${baseUrl}/gyms/exercises`, body, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
patchExerciseCoach: (body: ExerciseCoachBody, id: string) => axios.patch<Record<'id' | 'message', string>>(`${baseUrl}/gyms/exercises/${id}`, body, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
postGroup: (body: GroupGymBody) => axios.post(`${baseUrl}/groups-gym`, body, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
patchGroup: (id: string, body: GroupGymBody) => axios.patch(`${baseUrl}/groups-gym/${id}`, body, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
getGroupsGym: () => axios.get(`${baseUrl}/groups-gym`, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
getGroupGym: (id: string) => axios.get<GroupGym>(`${baseUrl}/groups-gym/${id}`, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
postWorkoutUser: (body: Workout) => axios.post(`${baseUrl}/workouts`, body, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
deleteExercise: (id: string) => axios.delete(`${baseUrl}/gyms/exercises/${id}`, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
deleteGymUser: (id: string, idUser: string) => axios.delete(`${baseUrl}/gyms/${id}/users/${idUser}`, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
deleteGroupGym: (id: string) => axios.delete(`${baseUrl}/groups-gym/${id}`, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
confirmCoach: (body: {token: string, password: string}) => axios.post(`${baseUrl}/coaches-auth/confirm`, body, {headers: {Authorization: `Basic ${basicAuth}`}}),
confirmUser: (body: {token: string, password: string}) => axios.post(`${baseUrl}/coaches-auth/confirm-user`, body, {headers: {Authorization: `Basic ${basicAuth}`}}),
confirmUserGym: (body: {token: string }) => axios.post(`${baseUrl}/coaches-auth/confirm-user-gym`, body, {headers: {Authorization: `Basic ${basicAuth}`}}),
patchImageCoach: (formData: FormData) => axios.post<{image: string}>(`${baseUrl}/gyms/image/${getStorageItem('coachMe')?.coach._id}`, formData, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
// BLOCKS
postBlockCoach: (body: any) => axios.post<Record<'id' | 'message', string>>(`${baseUrl}/gyms/blocks`, body, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
patchBlockCoach: (id: string, body: any) => axios.patch<Record<'id' | 'message', string>>(`${baseUrl}/gyms/blocks/${id}`, body, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
getBlocksCoach: (params?: Record<'limit' | 'skip', number> & {language?: Languages, type?: BlockType, common?: boolean, search?: string, add__exercises?: boolean, add__tags?: boolean}) => axios.get<EndpointWithPagination<BlockProgram>>(`${baseUrl}/gyms/blocks`, {params, headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
getBlockCoach: (id: string) => axios.get<BlockProgram>(`${baseUrl}/gyms/blocks/${id}`, { headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
deleteBlockCoach: (id: string) => axios.delete(`${baseUrl}/gyms/blocks/${id}`, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
// programs
postProgramCoach: (body: any) => axios.post<Record<'id' | 'message', string>>(`${baseUrl}/gyms/programs`, body, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
getProgramsCoach: (params?: Record<'limit' | 'skip', number> & {language?: Languages, type?: BlockProgram, common?: boolean, search?: string, add__exercises?: boolean, add__tags?: boolean}) => axios.get<EndpointWithPagination<IProgram>>(`${baseUrl}/gyms/programs`, {params, headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
getProgramCoach: (id: string, params?: {language?: Languages, add__exercises?: boolean, add__tags?: boolean}) => axios.get<IProgram>(`${baseUrl}/gyms/programs/${id}`, {params, headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
patchProgramCoach: (id: string,body: any) => axios.patch<Record<'id' | 'message', string>>(`${baseUrl}/gyms/programs/${id}`, body, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),


deleteGymEntity: (id: string, entity: "block" | "programs") => axios.delete(`${baseUrl}/gyms/${entity}/${id}`, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),

// programs
postActivityCoach: (body: any) => axios.post<Record<'id' | 'message', string>>(`${baseUrl}/gyms/activity`, body, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
getActivitiesCoach: (params: any) => axios.get<EndpointWithPagination<any>>(`${baseUrl}/gyms/activities`, {params, headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
getActivityCoach: (id: string) => axios.get<any>(`${baseUrl}/gyms/activities/${id}`, { headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),
deleteActivitiesCoach: (id: string) => axios.delete(`${baseUrl}/gyms/activities/${id}`, {headers: {Authorization: `Bearer ${getStorageItem('coachMe')?.accessToken}`}}),


userLogin: (body: {email: string, password: string}) => axios.post<IAxiosResponses["userLogin"]>(`${baseUrl}/users-auth/login`, body, {headers: {Authorization: `Basic ${basicAuth}`}}),


test: () => axios.get<IAxiosResponses["userLogin"]>(`${baseUrl}/teams/68680ab560690cc888943738/details`, {headers: {Authorization: `Basic ${basicAuth}`}}),
teamDetails: (id:string) => axios.get<IDetailsTeamResponse>(`${baseUrl}/teams/${id}/details`, {headers: {Authorization: `Basic ${basicAuth}`}}),
/**
 * Stato della squadra oggi, in categorie che si escludono a vicenda:
 * infortunati > a rischio > senza risposta > disponibili.
 * "Disponibile" richiede l'Hooper compilato: senza, il rischio non è noto.
 */
teamPulse: (teamId:string) => axios.get<ITeamPulse>(`${baseUrl}/teams/pulse`, {params: {teamId}, headers: {Authorization: `Basic ${basicAuth}`}}),
getTeamStats: (id: string, params?: { startDate?: string; endDate?: string }) => axios.get<any>(`${baseUrl}/teams/${id}/stats`, { params }),
getUserDetails: (id: string) => axios.get<any>(`${baseUrl}/users/${id}/details`, {headers: {Authorization: `Basic ${basicAuth}`}}),
updateCertificateExpiry: (userId: string, medicalCertificateExpiry: string | null) =>
  axios.patch(`${baseUrl}/users/${userId}/certificate`, { medicalCertificateExpiry }),

// -------- TESTING MODULE -------- //
getTestCatalog: (params?: { category?: string }) => axios.get<ITestDefinition[]>(`${baseUrl}/testing/catalog`, { params }),
// Catalogo unificato: pesca dagli esercizi con Cat.Testing (1143), non dalla lista legacy 107.
getUnifiedCatalog: (params?: { category?: string }) => axios.get<ITestDefinition[]>(`${baseUrl}/testing/catalog/unified`, { params }),
getTestDefinition: (testId: string) => axios.get<ITestDefinition | null>(`${baseUrl}/testing/catalog/${testId}`),
getLatest1RM: (params: { athleteId: string; exerciseId: string }) => axios.get<{ value: number | null; unit: string; date: string | null; isPR?: boolean; sourceType?: string; resultId?: string }>(`${baseUrl}/testing/latest-1rm`, { params }),
getTeamTestConfig: (teamId: string) => axios.get<ITeamTestConfig | null>(`${baseUrl}/testing/config/${teamId}`),
updateTeamTestConfig: (teamId: string, body: Omit<ITeamTestConfig, '_id' | 'teamId'>) => axios.put<ITeamTestConfig>(`${baseUrl}/testing/config/${teamId}`, body),
postTestResult: (body: { testId: string; athleteId: string; teamId: string; date: string; value?: number; unit?: string; laterality?: string; notes?: string; exerciseId?: string; protocolId?: string; values?: Record<string, number>; isComplete?: boolean; missingFields?: string[] }) => axios.post<ITestResult>(`${baseUrl}/testing/results`, body),
getAthleteTestResults: (athleteId: string, params: { testId: string; limit?: number }) => axios.get<ITestResult[]>(`${baseUrl}/testing/results/athlete/${athleteId}`, { params }),
getTeamTestResults: (teamId: string, params: { testId: string; date: string }) => axios.get<ITestResult[]>(`${baseUrl}/testing/results/team/${teamId}`, { params }),
deleteTestResult: (resultId: string) => axios.delete(`${baseUrl}/testing/results/${resultId}`),
getTestingAnalytics: (athleteId: string, teamId: string, testId: string) => axios.get<ITestingAnalytics>(`${baseUrl}/testing/analytics/${athleteId}/${teamId}/${testId}`),
postRTPProtocol: (body: { athleteId: string; teamId: string; injuryDate: string; injuryType: string; injurySide: string; enabledTests?: string[]; notes?: string }) => axios.post<IRTPProtocol>(`${baseUrl}/testing/rtp`, body),
getAthleteRTP: (athleteId: string) => axios.get<IRTPProtocol | null>(`${baseUrl}/testing/rtp/${athleteId}`),
getAthleteRTPCriteria: (athleteId: string) => axios.get<IRTPPhaseStatus[]>(`${baseUrl}/testing/rtp/${athleteId}/criteria`),
completeRTPProtocol: (protocolId: string) => axios.put(`${baseUrl}/testing/rtp/${protocolId}/complete`),

// -------- PROGRAMMING MODULE -------- //

// Esercizi (catalogo read-only)
getProgrammingExercises: (params?: { group?: string; search?: string; lang?: string; common?: boolean; page?: number; limit?: number }) =>
  axios.get<IProgrammingPaginated<IProgrammingExercise>>(`${baseUrl}/programming/exercises`, { params }),
getProgrammingExercise: (id: string) =>
  axios.get<IProgrammingExercise>(`${baseUrl}/programming/exercises/${id}`),
getProgrammingExerciseGroups: () =>
  axios.get<IProgrammingGroupExercise[]>(`${baseUrl}/programming/exercise-groups`),

// Blocchi
postProgrammingBlock: (body: Partial<IBlock>) =>
  axios.post<IBlock>(`${baseUrl}/programming/blocks`, body),
getProgrammingBlocks: (params?: { type?: string; isTemplate?: boolean; isLeopartTemplate?: boolean; search?: string; createdBy?: string; page?: number; limit?: number }) =>
  axios.get<IProgrammingPaginated<IBlock>>(`${baseUrl}/programming/blocks`, { params }),
getProgrammingBlock: (id: string) =>
  axios.get<IBlock>(`${baseUrl}/programming/blocks/${id}`),
patchProgrammingBlock: (id: string, body: Partial<IBlock>) =>
  axios.patch<IBlock>(`${baseUrl}/programming/blocks/${id}`, body),
duplicateProgrammingBlock: (id: string) =>
  axios.post<IBlock>(`${baseUrl}/programming/blocks/${id}/duplicate`),
deleteProgrammingBlock: (id: string) =>
  axios.delete(`${baseUrl}/programming/blocks/${id}`),

// Schede (Workout)
postProgrammingWorkout: (body: Partial<IWorkout>) =>
  axios.post<IWorkout>(`${baseUrl}/programming/workouts`, body),
getProgrammingWorkouts: (params?: { type?: string; isTemplate?: boolean; isLeopartTemplate?: boolean; search?: string; tags?: string; createdBy?: string; page?: number; limit?: number }) =>
  axios.get<IProgrammingPaginated<IWorkout>>(`${baseUrl}/programming/workouts`, { params }),
getProgrammingWorkout: (id: string) =>
  axios.get<IWorkout>(`${baseUrl}/programming/workouts/${id}`),
patchProgrammingWorkout: (id: string, body: Partial<IWorkout>) =>
  axios.patch<IWorkout>(`${baseUrl}/programming/workouts/${id}`, body),
duplicateProgrammingWorkout: (id: string) =>
  axios.post<IWorkout>(`${baseUrl}/programming/workouts/${id}/duplicate`),
deleteProgrammingWorkout: (id: string) =>
  axios.delete(`${baseUrl}/programming/workouts/${id}`),

// Assegnazioni
postProgrammingAssignment: (body: Partial<IWorkoutAssignment>) =>
  axios.post<IWorkoutAssignment>(`${baseUrl}/programming/assignments`, body),
getProgrammingAssignments: (params?: { teamId?: string; athleteId?: string; from?: string; to?: string; page?: number; limit?: number }) =>
  axios.get<IProgrammingPaginated<IWorkoutAssignment>>(`${baseUrl}/programming/assignments`, { params }),
getProgrammingAssignment: (id: string) =>
  axios.get<IWorkoutAssignment>(`${baseUrl}/programming/assignments/${id}`),
patchProgrammingAssignment: (id: string, body: Partial<IWorkoutAssignment>) =>
  axios.patch<IWorkoutAssignment>(`${baseUrl}/programming/assignments/${id}`, body),
deleteProgrammingAssignment: (id: string) =>
  axios.delete(`${baseUrl}/programming/assignments/${id}`),

// Occorrenze (per-data)
getAssignmentOccurrences: (assignmentId: string) =>
  axios.get(`${baseUrl}/programming/assignments/${assignmentId}/occurrences`),
getAssignmentOccurrence: (assignmentId: string, eventId: string) =>
  axios.get(`${baseUrl}/programming/assignments/${assignmentId}/occurrences/${eventId}`, {
    params: { populate: 'users.firstName,users.lastName,users.email,users:image.image' },
  }),
patchAssignmentOccurrence: (assignmentId: string, eventId: string, scope: 'single' | 'this_and_future' | 'all', body: any) =>
  axios.patch(`${baseUrl}/programming/assignments/${assignmentId}/occurrences/${eventId}`, body, { params: { scope } }),
deleteAssignmentOccurrence: (assignmentId: string, eventId: string, scope: 'single' | 'this_and_future' | 'all') =>
  axios.delete(`${baseUrl}/programming/assignments/${assignmentId}/occurrences/${eventId}`, { params: { scope } }),

// Log (esecuzione atleta)
postWorkoutLog: (body: { workoutAssignmentId: string; athleteId: string; dayNumber: number; date: string; eventId?: string }) =>
  axios.post<IWorkoutLog>(`${baseUrl}/programming/logs`, body),
patchWorkoutLog: (logId: string, body: Partial<IWorkoutLog>) =>
  axios.patch<IWorkoutLog>(`${baseUrl}/programming/logs/${logId}`, body),
completeWorkoutLog: (logId: string, body: { rpe: number }) =>
  axios.post<IWorkoutLog>(`${baseUrl}/programming/logs/${logId}/complete`, body),
getAthleteLogs: (athleteId: string, params?: { from?: string; to?: string; status?: string; workoutAssignmentId?: string; eventId?: string; page?: number; limit?: number }) =>
  axios.get<IProgrammingPaginated<IWorkoutLog>>(`${baseUrl}/programming/logs/athlete/${athleteId}`, { params }),
getWorkoutLog: (logId: string) =>
  axios.get<IWorkoutLog>(`${baseUrl}/programming/logs/${logId}`),

// Feedback
postAthleteFeedback: (body: { athleteId: string; workoutLogId: string; exerciseId: string; type: string; message?: string }) =>
  axios.post<IAthleteFeedback>(`${baseUrl}/programming/feedback`, body),
getAthleteFeedback: (athleteId: string, params?: { type?: string }) =>
  axios.get<IAthleteFeedback[]>(`${baseUrl}/programming/feedback/${athleteId}`, { params }),

// Analytics
getSessionAnalytics: (logId: string) =>
  axios.get<ISessionAnalytics>(`${baseUrl}/programming/analytics/session/${logId}`),
getAthleteAnalytics: (athleteId: string, params?: { from?: string; to?: string }) =>
  axios.get<IAthleteAnalytics>(`${baseUrl}/programming/analytics/athlete/${athleteId}`, { params }),
getTeamAnalytics: (teamId: string, params?: { from?: string; to?: string }) =>
  axios.get<ITeamAnalytics>(`${baseUrl}/programming/analytics/team/${teamId}`, { params }),

// Storico pesi
getWeightHistory: (athleteId: string, exerciseId: string, params?: { limit?: number }) =>
  axios.get<IWeightHistory>(`${baseUrl}/programming/weight-history/${athleteId}/${exerciseId}`, { params }),

// RTP Session Logs
postRtpSessionLog: (body: { athleteId: string; teamId: string; workoutLogId?: string; painNRS: number; painZones: string[]; confidenceScore: number; fearScore: number; notes?: string }) =>
  axios.post(`${baseUrl}/programming/rtp-session-logs`, body),
getAthleteRtpSessionLogs: (athleteId: string, params?: { page?: number; limit?: number }) =>
  axios.get(`${baseUrl}/programming/rtp-session-logs/athlete/${athleteId}`, { params }),
getRtpSessionTrends: (athleteId: string, params?: { from?: string; to?: string }) =>
  axios.get(`${baseUrl}/programming/rtp-session-logs/trends/${athleteId}`, { params }),

// Weight History (extended)
getWeightHistorySummary: (athleteId: string, params?: { from?: string; to?: string }) =>
  axios.get(`${baseUrl}/programming/weight-history/${athleteId}/summary`, { params }),
getTeamWeightHistory: (teamId: string, exerciseId: string, params?: { from?: string; to?: string }) =>
  axios.get(`${baseUrl}/programming/weight-history/team/${teamId}/${exerciseId}`, { params }),

// ── Match Sessions (Fase 4) ──────────────────────────────────────────────────
listMatchSessions: (params?: { teamId?: string; startDate?: string; endDate?: string }) =>
  axios.get(`${baseUrl}/match-sessions`, { params }),
getMatchSession: (id: string) =>
  axios.get(`${baseUrl}/match-sessions/${id}`),
createMatchSession: (body: Record<string, unknown>) =>
  axios.post(`${baseUrl}/match-sessions`, body),
updateMatchSession: (id: string, body: Record<string, unknown>) =>
  axios.patch(`${baseUrl}/match-sessions/${id}`, body),
deleteMatchSession: (id: string) =>
  axios.delete(`${baseUrl}/match-sessions/${id}`),
saveFormation: (id: string, body: { template?: string; startingLineup: unknown[]; bench?: unknown[] }) =>
  axios.post(`${baseUrl}/match-sessions/${id}/formation`, body),
addMatchSubstitution: (id: string, body: { minute?: number; playerIn: string; playerOut: string; reason?: string; notes?: string }) =>
  axios.post(`${baseUrl}/match-sessions/${id}/substitutions`, body),
getLineupHistory: (id: string, params: { teamId: string; limit?: number }) =>
  axios.get(`${baseUrl}/match-sessions/${id}/lineup-history`, { params }),
savePostMatch: (id: string, body: { result?: Record<string, unknown>; minutesPlayed?: unknown[]; postMatchNotes?: string; matchStats?: Record<string, number>; gpsSessionId?: string }) =>
  axios.post(`${baseUrl}/match-sessions/${id}/post-match`, body),
recalcMinutesPlayed: (id: string, totalMinutes?: number) =>
  axios.post(`${baseUrl}/match-sessions/${id}/minutes-played/recalc`, { totalMinutes }),
saveMatchAsTemplate: (id: string, templateName: string) =>
  axios.post(`${baseUrl}/match-sessions/${id}/save-as-template`, { templateName }),

// ── Coach Sessions (Fase 4) ──────────────────────────────────────────────────
getCoachSessionById: (id: string) =>
  axios.get(`${baseUrl}/coach-sessions/${id}`),

// ── Coach Sessions Post-sessione (Fase 4) ────────────────────────────────────
saveSrpe: (id: string, body: { avgAthleteRpe: number }) =>
  axios.post(`${baseUrl}/coach-sessions/${id}/srpe`, body),
saveDrpe: (id: string, body: { avgAthleteDRpe: { respiratory?: number; muscular?: number; mental?: number } }) =>
  axios.post(`${baseUrl}/coach-sessions/${id}/drpe`, body),
saveCalibration: (id: string, body: { coachRatingEffectiveness?: number; calibrationStatus?: string }) =>
  axios.post(`${baseUrl}/coach-sessions/${id}/calibration`, body),

// ── Coach Sessions GPS (Fase 6) ──────────────────────────────────────────────
getCoachSessionGpsSummary: (id: string) =>
  axios.get(`${baseUrl}/coach-sessions/${id}/gps-summary`),
matchCoachSessionGps: (id: string) =>
  axios.post(`${baseUrl}/coach-sessions/${id}/match-gps`, {}),
calibrateCoachSessionIps: (id: string) =>
  axios.post(`${baseUrl}/coach-sessions/${id}/calibrate-ips`, {}),

// ── Field Programming (Programmazione Campo) ─────────────────────────────────
fieldProgramming: fieldProgrammingEndpoints,

// ── Tactical Schemas (Lavagna Tattica) ───────────────────────────────────────
tacticalSchemas: buildTacticalSchemasEndpoints(axios),

}), [])


export type IEndpoints = ReturnType<typeof useEndpoints>
